import { useEffect, useState, useMemo, useCallback } from "react";
import dynamic from "next/dynamic";
import { Provider } from "react-redux";
import Box from "@mui/material/Box";
import { useRouter } from "next/router";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import Head from "next/head";
import { ToastContainer, Slide } from "react-toastify";

import store, { userAction, localAction } from "@/Redux/store";
import { arFont, enFont } from "@/styles/font";
import { navigation } from "@/utils/const";
import { fetchProducts } from "@/utils/general-functions";
import { I18nProvider } from "@/i18n/I18nProvider";
import { useAOS } from "@/hooks/useAOS";

import Navbar from "@/components/navbar";
import UserNavbar from "@/components/UserNavbar/UserNavbar";
import FeedbackButton from "@/components/feedBack/FeedBackButton.jsx";
import FeedBack from "@/components/feedBack/FeedBack.jsx";
import BlockBrowserZoom from "@/components/BlockBrowserZoom";

import DelayedTrackers, {
  FACEBOOK_PIXEL_ID,
} from "@/components/analytics/DelayedTrackers";

import "react-toastify/dist/ReactToastify.css";
import "@/styles/globals.css";

const Footer = dynamic(() => import("@/components/Footer.jsx"), {
  ssr: false,
});

const NoInternetConnection = dynamic(
  () => import("@/components/NoInternetConnection"),
  {
    ssr: false,
  },
);

const PROFILE_PATHS = new Set([
  "/contract",
  "/profile",
  "/customerservices",
]);

const theme = createTheme({
  typography: {
    fontFamily: "inherit",
  },

  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "capitalize",

          "@media (min-width:1200px)": {
            fontSize: "16px !important",
          },

          fontWeight: "600 !important",
        },
      },
    },
  },
});

const EMPTY_PRODUCTS = [];

const App = ({ Component, pageProps }) => {
  const router = useRouter();
  const { locale } = router;

  const allProduct = useMemo(
    () => pageProps?.allProduct || EMPTY_PRODUCTS,
    [pageProps?.allProduct],
  );

  const [openFeedback, setOpenFeedback] = useState(false);

  const [isOnline, setIsOnline] = useState(() => {
    if (typeof window !== "undefined") {
      return navigator.onLine;
    }

    return true;
  });

  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useAOS(undefined, router.asPath, {
    minDelayMs: 2000,
    idleTimeoutMs: 4000,
    refreshDelayMs: 300,
  });

  useEffect(() => {
    if (allProduct.length === 0) return;

    const write = () => {
      try {
        localStorage.setItem(
          "allProducts",
          JSON.stringify(allProduct),
        );
      } catch {
        // Ignore localStorage errors.
      }
    };

    if (
      typeof window !== "undefined" &&
      "requestIdleCallback" in window
    ) {
      const idleId = window.requestIdleCallback(write, {
        timeout: 4000,
      });

      return () => {
        window.cancelIdleCallback?.(idleId);
      };
    }

    const timeoutId = setTimeout(write, 1200);

    return () => clearTimeout(timeoutId);
  }, [allProduct]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const currentPage = window.location.href;
    const lastVisitedPage = localStorage.getItem("lastVisitedPage");

    if (
      !lastVisitedPage ||
      !currentPage.includes("no-internet-connection")
    ) {
      localStorage.setItem("lastVisitedPage", currentPage);
    }
  }, [isOnline]);

  useEffect(() => {
    store.dispatch(localAction.update(locale));
  }, [locale]);

  const isProfilePath = PROFILE_PATHS.has(router.pathname);

  useEffect(() => {
    localStorage.setItem(
      "bro-to",
      process.env.NEXT_PUBLIC_BRO_TOKEN,
    );
  }, []);

  useEffect(() => {
    setIsLoggedIn(Boolean(localStorage.getItem("UT_UI")));
  }, [router.asPath]);

  useEffect(() => {
    const existingProducts = sessionStorage.getItem("Products");

    if (existingProducts && existingProducts !== "null") {
      return;
    }

    const run = () => fetchProducts();

    if ("requestIdleCallback" in window) {
      const idleId = window.requestIdleCallback(run, {
        timeout: 3000,
      });

      return () => {
        window.cancelIdleCallback?.(idleId);
      };
    }

    const timeoutId = setTimeout(run, 2500);

    return () => clearTimeout(timeoutId);
  }, []);

  const logOut = useCallback(() => {
    localStorage.removeItem("UT_UI");

    setIsLoggedIn(false);

    store.dispatch(
      userAction.update({
        key: "name",
        val: "",
      }),
    );

    store.dispatch(
      userAction.update({
        key: "avatar",
        val: "",
      }),
    );

    store.dispatch(
      userAction.update({
        key: "nationalId",
        val: "",
      }),
    );

    store.dispatch(
      userAction.update({
        key: "wallet",
        val: "",
      }),
    );
  }, []);

  navigation.router = router;

  const isBranchesVideo =
    router.pathname === "/branches-video" ||
    router.asPath.includes("branches-video");

  const toggleFeedback = useCallback(() => {
    setOpenFeedback((prev) => !prev);
  }, []);

  const closeFeedback = useCallback(() => {
    setOpenFeedback(false);
  }, []);

  const pageElement = useMemo(
    () => <Component {...pageProps} />,
    [Component, pageProps],
  );

  return (
    <ThemeProvider theme={theme}>
      <I18nProvider>
        <Provider store={store}>
          <Head>
            <meta name="theme-color" content="#ffffff" />

            <meta
              name="viewport"
              content="width=device-width, initial-scale=1, viewport-fit=cover"
            />

            <meta
              name="facebook-domain-verification"
              content="lca6h42e5arwz7hxo210m2tb9yhhqr"
            />

            <link
              rel="icon"
              href="https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/favicon.ico"
            />
          </Head>

          <noscript>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              height="1"
              width="1"
              style={{ display: "none" }}
              src={`https://www.facebook.com/tr?id=${FACEBOOK_PIXEL_ID}&ev=PageView&noscript=1`}
              alt=""
            />
          </noscript>

          <DelayedTrackers replayDelayMs={12000} />

          {isBranchesVideo ? (
            pageElement
          ) : (
            <Box
              id="app-root"
              dir={locale === "en" ? "ltr" : "rtl"}
              className={`${arFont.variable} ${enFont.variable}`}
            >
              {isProfilePath ? (
                <UserNavbar onLogout={logOut} />
              ) : (
                <Navbar
                  isLoggedIn={isLoggedIn}
                  onLogout={logOut}
                  allProduct={allProduct}
                />
              )}

              {!isOnline ? (
                <NoInternetConnection />
              ) : (
                <>
                  <BlockBrowserZoom />

                  {pageElement}

                  {!isBranchesVideo && (
                    <FeedbackButton
                      onToggle={toggleFeedback}
                      isOpen={openFeedback}
                    />
                  )}

                  {openFeedback && (
                    <FeedBack onClose={closeFeedback} />
                  )}

                  <ToastContainer
                    position="top-center"
                    autoClose={3000}
                    rtl={locale !== "en"}
                    pauseOnHover
                    transition={Slide}
                    theme="colored"
                  />
                </>
              )}

              <Footer />
            </Box>
          )}
        </Provider>
      </I18nProvider>
    </ThemeProvider>
  );
};

export default App;